import TextField, { TextFieldProps } from '@mui/material/TextField'
import { Controller, FieldError, RegisterOptions } from 'react-hook-form'
import FieldLabel from 'src/components/Inputs/FieldLabel'

const SHARED_TEXTFIELD_CLASSES = [
  '[&_.MuiInputBase-root]:text-ink-700',
  '[&_.MuiInputBase-root]:font-normal',
  '[&_.MuiInputBase-root]:bg-white-500',
  '[&_.MuiInputBase-root]:rounded-[10px]',
  '[&_.MuiInputBase-root]:border',
  '[&_.MuiInputBase-root]:border-solid',
  '[&_.MuiInputBase-root]:border-ink-100/40',
  '[&_.MuiInputBase-root]:py-[10px]',
  '[&_.MuiInputBase-root]:px-[14px]',
  '[&_.MuiInputBase-root]:transition-[border-color,box-shadow]',
  '[&_.MuiInputBase-root]:duration-200',
  '[&_.MuiInputBase-root]:ease-linear',
  '[&_.MuiInputBase-root::placeholder]:text-ink-200',
  '[&_.MuiInputBase-root:hover]:border-ink-100/60',
  // Read-only must LOOK read-only — hosts dead-clicked disabled fields that
  // were pixel-identical to editable ones (wizard + inventory, 2026-09).
  '[&_.MuiInputBase-root.Mui-disabled]:bg-cream-400',
  '[&_.MuiInputBase-root.Mui-disabled:hover]:border-ink-100/40',
  '[&_.MuiInputBase-root.Mui-disabled]:cursor-not-allowed',
  '[&_.MuiInputBase-root.Mui-focused]:border-blue-500',
  '[&_.MuiInputBase-root.Mui-focused]:shadow-[0_0_0_3px_#D8F1FF]',
  '[&_.MuiInputBase-root::before]:border-none!',
  '[&_.MuiInputBase-root::after]:border-none!',
  '[&_.MuiInputBase-root_fieldset]:border-none',
  '[&_.MuiInputBase-root_textarea]:border-none',
  '[&_.MuiInputBase-root_.MuiInputBase-input]:p-0',
  // iOS Safari zooms inputs with font-size < 16px on focus. Pin to 16px to
  // prevent the zoom; consumers can override via `slotProps.htmlInput.style`.
  '[&_.MuiInputBase-root_.MuiInputBase-input]:text-base'
].join(' ')

const ERROR_TEXTFIELD_CLASSES = [
  '[&_.MuiInputBase-root]:border-red-500',
  '[&_.MuiInputBase-root:hover]:border-red-500',
  '[&_.MuiInputBase-root.Mui-focused]:border-red-500',
  '[&_.MuiInputBase-root.Mui-focused]:shadow-[0_0_0_3px_rgba(250,126,141,0.2)]'
].join(' ')

const BIGGER_EXTRA_CLASSES = [
  '[&_.MuiInputBase-root]:font-medium',
  '[&_.MuiInputBase-root_.MuiInputBase-input]:text-center',
  '[&_.MuiInputBase-root_.MuiInputBase-input]:text-[50px]'
].join(' ')

const StyledTextField = (props: TextFieldProps) => {
  const { className, multiline, error, ...rest } = props
  // A fixed-height root clips nothing but lets `helperText` spill over the
  // next row (the team fiscal modal's legal-name hint sat on top of the
  // "Régimen fiscal" label). Grow with the helper; stay 40px otherwise.
  const heightClass = multiline || rest.helperText ? 'h-auto' : 'h-[40px]'
  const innerHeight = multiline
    ? '[&_.MuiInputBase-root]:h-auto'
    : '[&_.MuiInputBase-root]:h-[40px]'
  const errorClass = error ? ERROR_TEXTFIELD_CLASSES : ''
  return (
    <TextField
      multiline={multiline}
      error={error}
      className={`${heightClass} ${innerHeight} ${SHARED_TEXTFIELD_CLASSES} ${errorClass} ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

interface StyledBiggerTextFieldProps extends Omit<TextFieldProps, 'error'> {
  error?: boolean
}

const StyledBiggerTextField = (props: StyledBiggerTextFieldProps) => {
  const { className, multiline, error, ...rest } = props
  const heightClass = multiline ? 'h-auto' : 'h-[72px]'
  const innerHeight = multiline
    ? '[&_.MuiInputBase-root]:h-auto'
    : '[&_.MuiInputBase-root]:h-[72px]'
  const errorClass = error ? ERROR_TEXTFIELD_CLASSES : ''
  return (
    <TextField
      multiline={multiline}
      error={error}
      className={`${heightClass} ${innerHeight} ${SHARED_TEXTFIELD_CLASSES} ${BIGGER_EXTRA_CLASSES} ${errorClass} ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

type IProps = TextFieldProps & {
  id?: string
  topLabel?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: any
  error?: FieldError | boolean | undefined
  rules?: RegisterOptions
  bigger?: boolean
  limit?: number
  topDescription?: string
  topLabelIcon?: string
  alignCounter?: string
  hideCounter?: boolean
  /**
   * Visible reason under the field. `error` alone only paints the border, so
   * a `rules.validate` message never reached the user and the form refused to
   * save with nothing on screen (the 69-click "Guardar" burst, 2026-09-09).
   */
  errorMessage?: string
}

export default function BaseTextField({
  id,
  topLabel,
  control,
  rules,
  bigger,
  limit,
  topLabelIcon,
  topDescription,
  alignCounter,
  hideCounter,
  errorMessage,
  slotProps: callerSlotProps,
  // Fill the container by default. MUI's TextField is inline-flex at its
  // intrinsic ~230px otherwise, so any caller that forgot `fullWidth` rendered
  // a stubby input on mobile (e.g. the team invite form). Constrain width with
  // a wrapper instead; pass `fullWidth={false}` only for a deliberately inline field.
  fullWidth = true,
  ...textFieldProps
}: IProps) {
  const isMultiline = textFieldProps.multiline && !hideCounter
  const characterLimit = limit ? limit : textFieldProps.multiline ? 500 : 100

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const truncatedValue = event.target.value.slice(0, characterLimit)
    // Only write back when the limit actually cut something: a number input
    // reports "" for a half-typed value ("-", "1e"), and writing that ""
    // back erased what the user was typing.
    if (truncatedValue !== event.target.value) {
      event.target.value = truncatedValue
    }

    if (textFieldProps.onChange) {
      textFieldProps.onChange(event)
    }
  }
  const TextField = bigger ? StyledBiggerTextField : StyledTextField

  const renderCounter = (currentValue?: string) => {
    const displayedValue = currentValue ?? ''
    return `${displayedValue.length}/${characterLimit}`
  }

  const limitReached =
    (textFieldProps.value as string)?.length >= characterLimit

  const errorMessageId = errorMessage && id ? `${id}-error` : undefined
  const errorA11y = errorMessage
    ? { 'aria-invalid': true, 'aria-describedby': errorMessageId }
    : {}
  const errorMessageNode = errorMessage ? (
    <p
      id={errorMessageId}
      role="alert"
      className="mt-1.5 flex items-start gap-1.5 text-[12px] leading-snug text-ink-700"
    >
      <span
        className="mt-[5px] size-1.5 shrink-0 rounded-full bg-red-500"
        aria-hidden
      />
      {errorMessage}
    </p>
  ) : null

  return (
    <div>
      <FieldLabel
        topLabel={topLabel}
        topLabelIcon={topLabelIcon}
        topDescription={topDescription}
        required={textFieldProps.required}
        htmlFor={id}
      />

      {control && id ? (
        <div className="relative">
          <Controller
            name={id}
            control={control}
            rules={rules}
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  {...textFieldProps}
                  fullWidth={fullWidth}
                  // Forward an explicit, stable input id. Without it MUI calls
                  // React useId() for the <input> id, which serializes as
                  // `_R_<base32>` on the server and `_r_<n>` on the client;
                  // React.hydrateRoot only remaps those transparently when the
                  // tree shape matches, and on SSR'd public pages it often
                  // doesn't (dynamic() boundaries, breakpoint/auth-conditional
                  // renders above the form) → React 19 #418. Confirmed at-error
                  // on /pension-estacionamiento/* lead form (Trigger K). A
                  // semantic id ("name"/"email") is deterministic across SSR/CSR.
                  id={id}
                  onChange={(e) => {
                    handleInputChange(e)
                    field.onChange(e)
                  }}
                  slotProps={{
                    ...callerSlotProps,
                    htmlInput: {
                      maxLength: characterLimit,
                      // multiline → MUI wraps in TextareaAutosize which sets
                      // an inline style.height client-side via useLayoutEffect.
                      // SSR has nothing to measure → no inline style; CSR
                      // measures and writes `style="height: NNpx"`. That diff
                      // is a hard React 19 #418 on any SSR'd surface (search
                      // empty-state, listing pages, etc — see Datadog
                      // hydration_diff logs for /pension-estacionamiento/*).
                      // suppressHydrationWarning tells React to skip the
                      // comparison on this element; the autosize effect still
                      // runs and writes the height post-hydration.
                      ...(textFieldProps.multiline
                        ? { suppressHydrationWarning: true }
                        : {}),
                      ...errorA11y,
                      ...((
                        callerSlotProps as { htmlInput?: object } | undefined
                      )?.htmlInput ?? {})
                    }
                  }}
                  error={
                    !!(textFieldProps.error || errorMessage || limitReached)
                  }
                />
                {errorMessageNode}
                {isMultiline && (
                  <div
                    className={`absolute bottom-[-24px] ${alignCounter === 'left' ? '' : 'right-[15px]'} text-[12px] text-ink-200`}
                  >
                    {renderCounter(field.value)}
                  </div>
                )}
              </>
            )}
          />
        </div>
      ) : (
        <>
          <TextField
            {...textFieldProps}
            fullWidth={fullWidth}
            // Forward an explicit input id (see control-branch note above) so
            // MUI doesn't fall back to useId — avoids the SSR/CSR `_R_`/`_r_`
            // id drift (Trigger K). No-op when id is undefined.
            id={id}
            onChange={(e) => handleInputChange(e)}
            slotProps={{
              ...callerSlotProps,
              htmlInput: {
                maxLength: characterLimit,
                ...(textFieldProps.multiline
                  ? { suppressHydrationWarning: true }
                  : {}),
                ...errorA11y,
                ...((callerSlotProps as { htmlInput?: object } | undefined)
                  ?.htmlInput ?? {})
              }
            }}
            error={!!(textFieldProps.error || errorMessage || limitReached)}
          />
          {errorMessageNode}
          {isMultiline && (
            <div className="absolute right-[15px] bottom-[-20px] text-[12px] text-ink-200">
              {renderCounter((textFieldProps.value as string) ?? '')}
            </div>
          )}
        </>
      )}
    </div>
  )
}
